import React, { useState, useEffect } from "react";
import {
  Modal,
  Text,
  FormLayout,
  TextField,
  Checkbox,
  ButtonGroup,
  Button,
  InlineStack,
} from "@shopify/polaris";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "../css/AiSetup.css";

const USER_MESSAGES = {
  requiredUrl: "Please enter your website URL or select the checkbox below.",
  invalidUrl: "Please enter a valid website URL starting with https://",
  urlSpaces: "Website URL cannot contain spaces.",
  localhost:
    "Local/test URLs are not allowed. Please enter a public website URL.",
  competitorRequired: "Please enter a competitor website URL.",
  keywordsRequired: "Please add at least one product keyword.",
  ageRequired: "Please select at least one audience age range.",
  genderRequired: "Please select your target audience gender.",
  loadFailed:
    "We couldn’t load your saved setup details. Please refresh the page or try again.",
  saveFailed:
    "We couldn’t save your setup information. Please check your details and try again.",
};

const AiSetup = ({ shopOrigin, apiKey, open, setOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    shopUrl: "",
    noContent: false,
    competitorUrl: "",
    keywords: "",
    ageRanges: [],
    gender: "All Genders",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchPrefillData = async () => {
      if (shopOrigin) {
        try {
          const response = await axios.get(
            `/api/check-setup-status/?shop=${shopOrigin}`
          );
          const { initial_steps_completed, prefill } = response.data;

          if (
            initial_steps_completed ||
            location.search.includes("back=true")
          ) {
            const ageRanges = prefill.age_ranges
              ? prefill.age_ranges
                  .split(", ")
                  .map((range) => range.trim())
                  .filter(Boolean)
              : [];

            setFormData({
              shopUrl: prefill.shop_url || "",
              noContent: prefill.no_website_content || false,
              competitorUrl: prefill.competitor_url || "",
              keywords: prefill.keywords || "",
              ageRanges,
              gender: prefill.gender || "All Genders",
            });
            setStep(
              prefill.step1_completed && prefill.initial_steps_completed ? 2 : 1
            );
          }
        } catch (err) {
          console.error("Error fetching prefill data:", err);
          setErrors({ general: err.response?.data?.message || USER_MESSAGES.loadFailed });
        }
      }
    };

    fetchPrefillData();
  }, [shopOrigin, location.search]);

  const validateSingleField = (fieldId, value) => {
    const newErrors = { ...errors };

        const urlRegex = /^(https:\/\/)[^\s/$.?#].[^\s]*$/i;
    const localhostRegex =
      /^(http:\/\/localhost|http:\/\/127\.0\.0\.1|http:\/\/\d{1,3}(\.\d{1,3}){3})/;

    const handleUrlValidation = (value, key) => {
      if (/\s/.test(value)) {
        newErrors[key] = USER_MESSAGES.urlSpaces;
      } else if (localhostRegex.test(value)) {
        newErrors[key] = USER_MESSAGES.localhost;
      } else if (!value.startsWith("https://")) {
        newErrors[key] = USER_MESSAGES.invalidUrl;
      } else if (!urlRegex.test(value)) {
        newErrors[key] = USER_MESSAGES.invalidUrl;
      } else {
        delete newErrors[key];
      }
    };

    if (fieldId === "shopUrl" && value && !formData.noContent) {
      handleUrlValidation(value, "shopUrl");
    }

    if (fieldId === "competitorUrl" && value) {
      handleUrlValidation(value, "competitorUrl");
    }

    if (fieldId === "keywords" && !value.trim()) {
      delete newErrors.keywords;
    }

    if (!value) {
      delete newErrors[fieldId];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.shopUrl && !formData.noContent) {
      newErrors.shopUrl = USER_MESSAGES.requiredUrl;
      setErrors(newErrors);
      return false;
    }
      return validateSingleField("shopUrl", formData.shopUrl);
  };

  const validateStep2 = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.competitorUrl) {
      newErrors.competitorUrl = USER_MESSAGES.competitorRequired;
      isValid = false;
    } else {
      isValid =
        validateSingleField("competitorUrl", formData.competitorUrl) && isValid;
    }

    if (!formData.keywords.trim()) {
      newErrors.keywords = USER_MESSAGES.keywordsRequired;
      isValid = false;
    }

    if (formData.ageRanges.length === 0) {
      newErrors.ageRanges = USER_MESSAGES.ageRequired;
      isValid = false;
    }

    if (!formData.gender) {
      newErrors.gender = USER_MESSAGES.genderRequired;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field) => (value) => {
      setFormData((prev) => ({
        ...prev,
      [field]: value,
      ...(field === "shopUrl" && value.trim() && { noContent: false }),
      }));

      validateSingleField(field, value);
  };

  const handleAgeRangeChange = (value) => (checked) => {
    setFormData((prev) => ({
      ...prev,
      ageRanges: checked
        ? [...prev.ageRanges, value]
        : prev.ageRanges.filter((range) => range !== value),
    }));
    setErrors((prev) => ({ ...prev, ageRanges: "" }));
  };

  const handleGenderChange = (gender) => {
    setFormData((prev) => ({ ...prev, gender }));
    setErrors((prev) => ({ ...prev, gender: "" }));
  };

  const handleStep1Continue = () => {
    if (validateStep1()) setStep(2);
  };

  const handleStep2Back = () => {
    setStep(1);
    setErrors({});
  };

  const handleCreateArticle = async () => {
    if (!validateStep2()) return;

    const payload = {
      shop: shopOrigin,
      shop_url: formData.shopUrl,
      no_website_content: formData.noContent,
      competitor_url: formData.competitorUrl,
      keywords: formData.keywords,
      age_ranges: JSON.stringify(formData.ageRanges),
      gender: formData.gender,
      step1_completed: true,
      initial_steps_completed: true,
    };

    try {
      await axios.post("/api/save-setup/", payload, {
        headers: { "Content-Type": "application/json" },
      });
      setOpen(false);
      navigate(`/tone-content?shop=${encodeURIComponent(shopOrigin || "")}`);
    } catch (err) {
      console.error("Error saving setup:", err.response?.data || err.message);
      setErrors({ general: err.response?.data?.message || USER_MESSAGES.saveFailed });
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title={
        step === 1
          ? "Tell us about your Shop (Step 1 of 2)"
          : "Tell Us about your Competitors (Step 2 of 2)"
      }
      primaryAction={{
        content: step === 1 ? "Continue" : "Create Article",
        onAction: step === 1 ? handleStep1Continue : handleCreateArticle,
      }}
      secondaryActions={
        step === 2
          ? [{ content: "Back", onAction: handleStep2Back }]
          : undefined
      }
    >
      <Modal.Section>
                <FormLayout>
          {step === 1 && (
            <>
                  <TextField
                    label="Shopify Shop URL"
                    placeholder="e.g., https://www.example.com"
                    value={formData.shopUrl}
                    onChange={handleInputChange("shopUrl")}
                    error={errors.shopUrl}
                    autoComplete="off"
                    disabled={formData.noContent}
                  />
                  <Checkbox
                    label="I don't have any content on my website yet"
                    checked={formData.noContent}
                    onChange={handleInputChange("noContent")}
                    disabled={formData.shopUrl.trim().length > 0}
                  />
            </>
            )}

            {step === 2 && (
            <>
                  <TextField
                label="Competitor website"
                placeholder="https://www.example.com"
                    value={formData.competitorUrl}
                    onChange={handleInputChange("competitorUrl")}
                    error={errors.competitorUrl}
                helpText="Helps AI understand tone and style"
                  />
                  <TextField
                label="Product keywords"
                placeholder="e.g., lipstick, vegan skincare"
                    value={formData.keywords}
                    onChange={handleInputChange("keywords")}
                    error={errors.keywords}
                helpText="Comma separated"
                  />
                    <div>
                      <Text variant="bodyMd" fontWeight="medium">
                  Audience Age Range
                      </Text>
                      <InlineStack direction="column">
                  {["16-22", "23-32", "33-45", "46-60", "60+"].map((range) => (
                            <Checkbox
                              key={range}
                      label={range}
                              checked={formData.ageRanges.includes(range)}
                              onChange={handleAgeRangeChange(range)}
                            />
                  ))}
                        {errors.ageRanges && (
                    <Text color="critical">{errors.ageRanges}</Text>
                        )}
                      </InlineStack>
                    </div>
                    <div>
                      <Text variant="bodyMd" fontWeight="medium">
                  Audience Gender
                      </Text>
                      <ButtonGroup segmented>
                        {["Male", "Female", "All Genders"].map((gender) => (
                          <Button
                            key={gender}
                            pressed={formData.gender === gender}
                            onClick={() => handleGenderChange(gender)}
                          >
                            {gender}
                          </Button>
                        ))}
                      </ButtonGroup>
                {errors.gender && <Text color="critical">{errors.gender}</Text>}
              </div>
            </>
            )}
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
};

export default AiSetup;
